import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Container} from 'src/design/atoms/Container';
import {Divider} from 'src/design/atoms/Divider';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import type {UserInstanceModel} from 'src/api/ProactApiClient';
import {UserStatusEnum} from 'src/api/ProactApiClient';
import {InstancePicker} from 'src/app/security/authentication/InstancePicker';
import {Button} from 'src/design/atoms/Button';
import {Spinner} from 'src/design/atoms/Spinner';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {
  getUserInstances,
  setActiveInstance,
  validateUserSession,
  validateUserSessionSeamless,
} from 'src/redux/slices/sessionSlice';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {ServerErrorAlert} from 'src/app/layout/ServerErrorAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

import {IconLock, IconRefresh} from '@tabler/icons-react';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

type InstanceListStatus = 'loading' | 'ready' | 'failed';

const InactiveUserPage = () => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const {session, instance} = useAppSelector(state => state.session);
  const lockedCompanyName =
    session?.userStatus === UserStatusEnum.Inactive
      ? instance?.name
      : undefined;

  const [listStatus, setListStatus] = useState<InstanceListStatus>('loading');
  const [otherInstances, setOtherInstances] = useState<UserInstanceModel[]>([]);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string>();
  const [lastCheckedAt, setLastCheckedAt] = useState<Date>();

  // The persisted slice list can be stale, so the picker only uses a fresh fetch.
  const loadInstances = async () => {
    setListStatus('loading');
    try {
      const instances = await dispatch(getUserInstances()).unwrap();
      setOtherInstances(instances.filter(i => i.id !== instance?.id));
      setListStatus('ready');
    } catch {
      setListStatus('failed');
    }
  };

  useEffect(() => {
    void dispatch(validateUserSessionSeamless());
    void loadInstances();
  }, []);

  useEffect(() => {
    if (
      session?.userStatus != undefined &&
      session.userStatus !== UserStatusEnum.Inactive
    ) {
      navigate(PathUtility.Dashboard());
    }
  }, [session?.userStatus]);

  const handleRefresh = async () => {
    setError(undefined);
    setIsRefreshing(true);
    try {
      await dispatch(validateUserSession()).unwrap();
    } catch (refreshError) {
      setError(parseErrorMessage(refreshError));
      return;
    } finally {
      setIsRefreshing(false);
    }
    setLastCheckedAt(new Date());
    if (listStatus === 'failed') void loadInstances();
  };

  // The switch purges the session, so PrivateOutlet revalidates and routes back here if still inactive.
  const handleSwitch = async (instanceId: number) => {
    setError(undefined);
    setIsSwitching(true);
    try {
      await dispatch(setActiveInstance(instanceId)).unwrap();
    } catch (switchError) {
      setError(parseErrorMessage(switchError));
      setIsSwitching(false);
      return;
    }
    navigate(PathUtility.Dashboard());
  };

  const canSwitch = listStatus === 'ready' && otherInstances.length > 0;
  const showRefresh = !!session?.user && listStatus !== 'loading';

  const refreshBlock = showRefresh && (
    <Stack gap="xs">
      <Text c="dimmed" size="sm">
        {listStatus === 'failed'
          ? "We couldn't check your other companies. Refresh to try again."
          : 'Already unlocked? Refresh to check again.'}
      </Text>
      <Button
        fullWidth
        variant={canSwitch ? 'subtle' : 'filled'}
        color={canSwitch ? 'gray' : undefined}
        leftSection={<IconRefresh />}
        loading={isRefreshing}
        onClick={handleRefresh}>
        Refresh
      </Button>
      {lastCheckedAt && (
        <Text c="dimmed" size="sm" aria-live="polite">
          Last checked:{' '}
          {lastCheckedAt.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          })}
        </Text>
      )}
    </Stack>
  );

  return (
    <Container size={450} my={40}>
      <Stack>
        <Title ta="center">Inactive Account</Title>
        <Text c="dimmed" size="sm" ta="center">
          Your account is locked in {lockedCompanyName ?? 'this company'}.
        </Text>

        <Paper withBorder shadow="md" p={30} radius="md">
          <Stack gap="lg">
            <Alert
              variant="light"
              color="blue"
              icon={<IconLock />}
              title="Your administrator locked your account">
              You&apos;re still signed in, and this isn&apos;t an error. Contact
              your administrator to unlock your account in this company.
            </Alert>

            {listStatus === 'loading' && (
              <Spinner loadingText="Checking your other companies..." />
            )}

            {canSwitch && (
              <InstancePicker
                instances={otherInstances}
                onSelect={instanceId => void handleSwitch(instanceId)}
                isLoading={isSwitching}
                description="You can keep working in another company you have access to."
                submitLabel="Switch company"
              />
            )}

            {canSwitch && showRefresh && <Divider />}
            {refreshBlock}

            <ServerErrorAlert message={error} mt={0} />

            <Anchor
              component={Link}
              to={PathUtility.Logout()}
              aria-disabled={isSwitching}
              onClick={event => isSwitching && event.preventDefault()}
              size="sm"
              c="dimmed"
              ta="center">
              Sign Out
            </Anchor>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.InactiveUser(),
  element: <InactiveUserPage />,
  isPrivate: false,
});

export default InactiveUserPage;
